import React, { useState } from "react";
import LinksComponent from "../../../components/Tenant/LinksComponent/LinksComponent";
import { toast } from "react-hot-toast";
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./ManageMembership.css";
import { BsCheckCircleFill, BsCircle } from "react-icons/bs";
import {
  useCreateMembershipDetailsMutation,
  useUpdateMembershipDetailsMutation,
} from "../../../features/tenant/membershipDetails/membershipDetailsApi";
import { getVendorName } from "../../../utils/getVendorName";
import { isSchemeMembershipEnabled } from "../../../utils/schemeMembershipFlag";
import { useFetchMembershipByIdMutation } from "../../../features/tenant/membership/membershipApi";


const regularDataObj = {
  rangeStart: 0,
  rangeEnd: 100,
  perMonth: 0,
  pointsMultiplier: 0,
  giftDiscount: 0,
  cashback: false,
  cash_benefit: 0,
};

const schemeDataObj = {
  rangeStart: 0,
  rangeEnd: 100,
  bonusType: "fixed",
  bonus: 0,
};


// Rank colors indexed by (rank - 1), clamped to array length
const RANK_COLORS = [
  {
    header: "bg-amber-50 border-amber-200",
    name: "text-amber-800",
    badge: "bg-amber-100 text-amber-700 border border-amber-300",
  },
  {
    header: "bg-slate-50 border-slate-200",
    name: "text-slate-700",
    badge: "bg-slate-100 text-slate-600 border border-slate-300",
  },
  {
    header: "bg-orange-50 border-orange-200",
    name: "text-orange-800",
    badge: "bg-orange-100 text-orange-700 border border-orange-300",
  },
  {
    header: "bg-teal-50 border-teal-200",
    name: "text-teal-800",
    badge: "bg-teal-100 text-teal-700 border border-teal-300",
  },
];

function getRankColor(rank) {
  return RANK_COLORS[Math.min(rank - 1, RANK_COLORS.length - 1)];
}

function convertToTierFormat(dataArray, type = 1) {
  const result = {};

  // Sort by range_start ascending (lowest range = entry tier first).
  // Do NOT sort by tier.rank — rank values in the DB can be corrupted from old saves.
  const safeArray = dataArray
    .filter((e) => e.tier?.name)
    .sort((a, b) => Number(a.range_start) - Number(b.range_start));

  if (type === 0) {
    safeArray.forEach((entry) => {
      result[entry.tier.name] = {
        id: entry.id,
        rank: entry.tier.rank,
        rangeType: entry.range_type,
        rangeStart: entry.range_start,
        rangeEnd: entry.range_end,
        perMonth: entry.per_month || 0,
        pointsMultiplier: parseFloat(entry.points),
        giftDiscount: parseFloat(entry.gift_discount),
        cashback: entry.cashback,
        cash_benefit: parseFloat(entry.cash_benefit),
        bonusType: entry.tier.bonus_type || "fixed",
        bonus: parseFloat(entry.tier.bonus || 0),
      };
    });
  } else {
    safeArray.forEach((entry) => {
      result[entry.tier.name] = {
        rank: entry.tier.rank,
        rangeType: entry.range_type,
        rangeStart: entry.range_start,
        rangeEnd: entry.range_end,
        perMonth: entry.per_month || 0,
        pointsMultiplier: parseFloat(entry.points),
        giftDiscount: parseFloat(entry.gift_discount),
        cashback: entry.cashback,
        cash_benefit: entry.cash_benefit,
        bonusType: entry.tier.bonus_type || "fixed",
        bonus: parseFloat(entry.tier.bonus || 0),
      };
    });
  }

  return result;
}

function convertToDesiredFormat(obj, type = 1) {
  // Module-level helper, so it resolves the flag itself — it cannot see the
  // component-scoped const, and this runs on submit, long after login.
  const isSchemeMembership = isSchemeMembershipEnabled();
  const result = [];
  const keys = Object.keys(obj);
  const total = keys.length;

  if (type === 0) {
    // Recompute ranks from position in key order (ascending range → entry tier first).
    // position 0 = lowest range = rank `total` (entry tier), last = rank 1 (top tier).
    // This also heals any rank corruption left in the DB by old saves.
    keys.forEach((key, index) => {
      const tier = obj[key];
      const rank = total - index;
      if (isSchemeMembership) {
        result.push({
          id: tier.id,
          tier: {
            rank,
            name: key,
            bonus_type: "fixed",
            bonus: Number(tier.bonus),
          },
          range_start: tier.rangeStart,
          range_end: tier.rangeEnd,
          per_month: 0,
          points: 0,
          gift_discount: 0,
          cashback: false,
          cash_benefit: 0,
        });
      } else {
        result.push({
          id: tier.id,
          range_start: tier.rangeStart,
          range_end: tier.rangeEnd,
          per_month: tier.perMonth,
          points: tier.pointsMultiplier / 1,
          gift_discount: tier.giftDiscount / 1,
          cashback: tier.cashback,
          cash_benefit: tier.cash_benefit,
        });
      }
    });
  } else {
    let i = Object.keys(obj).length;
    for (let key in obj) {
      const tier = obj[key];
      if (isSchemeMembership) {
        result.push({
          tier: {
            rank: i--,
            name: key,
            bonus_type: "fixed",
            bonus: Number(tier.bonus),
          },
          range_start: tier.rangeStart,
          range_end: tier.rangeEnd,
          per_month: 0,
          points: 0,
          gift_discount: 0,
          cashback: false,
          cash_benefit: 0,
        });
      } else {
        result.push({
          tier: { rank: i--, name: key },
          range_start: tier.rangeStart,
          range_end: tier.rangeEnd,
          per_month: tier.perMonth,
          points: tier.pointsMultiplier / 1,
          gift_discount: tier.giftDiscount / 1,
          cashback: tier.cashback,
          cash_benefit: tier.cash_benefit,
        });
      }
    }
  }

  return result;
}

const inputCls =
  "w-28 text-center border border-gray-300 rounded-lg py-2 px-2 text-sm bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none";

const ManageMembership = () => {
  // Read at RENDER time: vendorData lands in sessionStorage during login,
  // so a module-level const captures the wrong value on first mount.
  const isSchemeMembership = isSchemeMembershipEnabled();
  // Depends on the flag, so it has to be resolved at render time too.
  const dataObj = isSchemeMembership ? schemeDataObj : regularDataObj;
  const navigate = useNavigate();
  const location = useLocation();

  const [rangeType, setRangeType] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [data, setData] = useState(null);
  const [isUpdate, setIsUpdate] = useState(false);

  const [
    addMembershipDetails,
    {
      data: addMembershipDetailsData,
      error: addMembershipDetailsError,
    },
  ] = useCreateMembershipDetailsMutation();

  const [
    updateMembershipDetails,
    { data: updateMembershipDetailsData, error: updateMembershipDetailsError },
  ] = useUpdateMembershipDetailsMutation();

  const [
    fetchMembershipById,
    { data: fetchMembershipData, error: fetchMembershipError },
  ] = useFetchMembershipByIdMutation();

  useEffect(() => {
    if (!location.state) return;

    fetchMembershipById({
      token: sessionStorage.getItem("access_token"),
      tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
      id: location.state.id,
    });

    let rangeTypeLabel = "Purchase";
    if (location.state?.range_type === 2) rangeTypeLabel = "Scan";
    else if (location.state?.range_type === 3) rangeTypeLabel = "QrMrp";
    else if (location.state?.range_type === 4) rangeTypeLabel = "Points";
    setRangeType(rangeTypeLabel);
    setTiers(location.state?.tiers);

    // Build initial data keyed by tier name, in rank order (highest rank number first)
    // reversed(tiers) puts the entry-level tier first so convertToDesiredFormat assigns it rank n
    const finalObj = {};
    const arr = [...location.state.tiers].reverse();
    arr.forEach((tier) => {
      finalObj[tier.name] = { rangeType: rangeTypeLabel, ...dataObj };
    });
    setData(finalObj);
  }, [location.state]);

  useEffect(() => {
    if (fetchMembershipData) {
      // getById does a LEFT JOIN — rows with tier=null mean no details saved yet
      const details = fetchMembershipData.body.filter(
        (row) => row.tier !== null && row.tier !== undefined,
      );
      if (details.length > 0) {
        setIsUpdate(true);
        setData(convertToTierFormat(details, 0));
      }
    }

    if (fetchMembershipError) {
      toast("Something went wrong, please refresh and try again", {
        id: "errroRefresh",
      });
    }
  }, [fetchMembershipData, fetchMembershipError]);

  function isValidRanges(obj) {
    let previousRangeEnd = -Infinity;

    // Sort by rangeStart ascending so validation is order-independent
    const entries = Object.entries(obj).sort(
      (a, b) => Number(a[1].rangeStart) - Number(b[1].rangeStart),
    );

    for (const [key, category] of entries) {
      if (category.rangeStart < 0 || category.rangeEnd < 0) {
        toast.error(`In ${key}, Start/End values cannot be negative.`, {
          id: "noNegetiveValues",
        });
        return false;
      }

      if (Number(category.rangeStart) >= Number(category.rangeEnd)) {
        toast.error(`In ${key}, Start Value must be smaller than End Value.`, {
          id: "inValidRangeStart",
        });
        return false;
      }

      if (Number(category.rangeStart) <= Number(previousRangeEnd)) {
        toast.error(
          `In ${key}, Start Value must be greater than the End Value of the previous tier.`,
          { id: "inValidRangeEnd" },
        );
        return false;
      }

      if (!isSchemeMembership) {
        if (
          category.pointsMultiplier / 1 < 0 ||
          category.pointsMultiplier / 1 > 100
        ) {
          toast.error(
            `In ${key}, Points Multiplier must be in the range [0, 100].`,
            { id: "pointsMultiplierError" },
          );
          return false;
        }

        if (category.cash_benefit / 1 < 0 || category.cash_benefit / 1 > 100) {
          toast.error(
            `In ${key}, Cash Benefit must be in the range [0, 100].`,
            { id: "cashBenefitError" },
          );
          return false;
        }

        if (category.giftDiscount / 1 < 0 || category.giftDiscount / 1 > 100) {
          toast.error(
            `In ${key}, Gift Discount must be in the range [0, 100].`,
            { id: "giftDiscountError" },
          );
          return false;
        }
      } else {
        if (Number(category.bonus) < 0) {
          toast.error(`In ${key}, Bonus must be a non-negative value.`, {
            id: "bonusError",
          });
          return false;
        }
      }

      previousRangeEnd = category.rangeEnd;
    }
    return true;
  }

  const handleOnValueChange = (value, tier, key) => {
    setData((prev) => ({
      ...prev,
      [tier]: { ...prev[tier], [key]: value },
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isValidRanges(data)) return;
    const res = convertToDesiredFormat(data);
    addMembershipDetails({
      token: sessionStorage.getItem("access_token"),
      tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
      body: {
        membership_id: location.state.id,
        data: res,
        qty: res.length,
      },
    });
  };

  const handleUpdate = (e) => {
    e.preventDefault();
    if (!isValidRanges(data)) return;
    const res = convertToDesiredFormat(data, 0);
    const reqData = res.map((each) => {
      const cleaned = { ...each };
      delete cleaned.created_at;
      delete cleaned.created_by_id;
      delete cleaned.created_by_name;
      delete cleaned.updated_at;
      delete cleaned.updated_by_id;
      delete cleaned.updated_by_name;
      delete cleaned.status;
      if (!isSchemeMembership) delete cleaned.tier;
      return cleaned;
    });

    updateMembershipDetails({
      token: sessionStorage.getItem("access_token"),
      tenant_id: JSON.parse(sessionStorage.getItem("vendorData")).tenant_id,
      body: {
        membership_id: location.state.id,
        batch: reqData,
      },
    });
  };

  useEffect(() => {
    if (addMembershipDetailsData) {
      toast.success("Membership created successfully", {
        id: "membershipDetailsCreated",
      });
      setTimeout(() => {
        navigate(`/genefied/${getVendorName()}/membership`);
      }, 400);
    }

    if (addMembershipDetailsError) {
      if (addMembershipDetailsError.status === 409) {
        toast.error("Membership already exists for this user type", {
          id: "addMembershipErrorAlreadyExists",
        });
      } else {
        toast.error("Something went wrong", { id: "addMembershipDetailsError" });
      }
    }
  }, [addMembershipDetailsData, addMembershipDetailsError]);

  useEffect(() => {
    if (updateMembershipDetailsData) {
      toast.success("Membership Updated successfully", {
        id: "membershipDetailsUpdated",
      });
      setTimeout(() => {
        navigate(`/genefied/${getVendorName()}/membership`);
      }, 400);
    }

    if (updateMembershipDetailsError) {
      toast.error("Something went wrong", { id: "updateMembershipDetailsError" });
    }
  }, [updateMembershipDetailsData, updateMembershipDetailsError]);

  // Sort tiers ascending by id → rank 1 = lowest id = highest tier
  // Display order: entry-level (rank n) on left, top tier (rank 1) on right
  const displayTiers = [...tiers].sort((a, b) => b.id - a.id);
  const tierRankMap = Object.fromEntries(
    [...tiers].sort((a, b) => a.id - b.id).map((t, i) => [t.name, i + 1]),
  );
  const totalTiers = tiers.length;

  return (
    <>
      <div className="flex w-full bg-gray-200 h-full justify-center">
        <div className="w-full">
          <div className="border-b bg-white border-gray-300 pl-6 py-2 shadow-sm text-xl font-bold">
            {isSchemeMembership ? "Manage Scheme Membership" : "Manage Membership"}
            <LinksComponent />
          </div>

          <div className="p-3">
            <form className="w-full bg-white shadow rounded-lg overflow-hidden">
              {/* Summary header */}
              <div className="flex flex-wrap justify-between items-center gap-3 bg-gray-50 border-b border-gray-200 px-4 py-3">
                <div className="space-y-0.5">
                  <p className="text-sm font-semibold text-gray-800 capitalize">
                    User Type:{" "}
                    <span className="text-blue-700">{location.state?.user_type}</span>
                  </p>
                  <p className="text-sm text-gray-500">
                    {isSchemeMembership
                      ? `Progression: ${
                          location.state?.mode === "periodic"
                            ? `Periodic · ${location.state?.period_days}-day periods`
                            : "Lifetime"
                        } · Metric: Scan Points`
                      : `Range Type: ${rangeType}`}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {displayTiers.map((tier) => {
                    const rank = tierRankMap[tier.name];
                    const colors = getRankColor(rank);
                    return (
                      <span
                        key={tier.name}
                        className={`text-xs font-semibold px-3 py-1 rounded-full ${colors.badge}`}
                      >
                        {tier.name} · Rank {rank}
                      </span>
                    );
                  })}
                </div>
              </div>

              {/* Tier configuration table */}
              {data && tiers && tiers.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-sm">
                    <thead>
                      <tr>
                        <th className="bg-gray-50 text-left px-5 py-3 font-semibold text-gray-600 border-b border-r border-gray-200 w-48">
                          Field
                        </th>
                        {displayTiers.map((tier) => {
                          const rank = tierRankMap[tier.name];
                          const colors = getRankColor(rank);
                          const isTop = rank === 1;
                          const isEntry = rank === totalTiers;
                          return (
                            <th
                              key={tier.name}
                              className={`${colors.header} border-b border-r border-gray-200 px-4 py-3 text-center`}
                            >
                              <div className="flex flex-col items-center gap-1">
                                <span className={`font-bold text-base ${colors.name}`}>
                                  {tier.name}
                                </span>
                                <span
                                  className={`text-xs font-medium px-2 py-0.5 rounded-full ${colors.badge}`}
                                >
                                  Rank {rank}
                                  {isTop ? " · Top Tier" : isEntry ? " · Entry Tier" : ""}
                                </span>
                              </div>
                            </th>
                          );
                        })}
                      </tr>
                    </thead>

                    <tbody>
                      {/* Slab range */}
                      <tr className="border-b border-gray-100">
                        <td className="bg-gray-50 px-5 py-4 font-semibold text-gray-700 border-r border-gray-200">
                          {isSchemeMembership ? "Scan Points Slab" : `${rangeType} Slab`}
                          <span className="block text-xs font-normal text-gray-400 mt-0.5">
                            Start — End
                          </span>
                        </td>
                        {displayTiers.map((tier) => (
                          <td
                            key={tier.name}
                            className="px-4 py-4 bg-white border-r border-gray-100 text-center"
                          >
                            <div className="flex items-center justify-center gap-2">
                              <input
                                type="number"
                                className={inputCls}
                                value={data[tier.name]?.rangeStart ?? 0}
                                onChange={(e) =>
                                  handleOnValueChange(e.target.value, tier.name, "rangeStart")
                                }
                              />
                              <span className="text-gray-400">—</span>
                              <input
                                type="number"
                                className={inputCls}
                                value={data[tier.name]?.rangeEnd ?? 100}
                                onChange={(e) =>
                                  handleOnValueChange(e.target.value, tier.name, "rangeEnd")
                                }
                              />
                            </div>
                          </td>
                        ))}
                      </tr>

                      {isSchemeMembership ? (
                        <>
                          {/* Bonus Amount — fixed flat points awarded once the tier
                              is reached. (Percentage bonus is not supported.) */}
                          <tr className="border-b border-gray-100">
                            <td className="bg-gray-50 px-5 py-4 font-semibold text-gray-700 border-r border-gray-200">
                              Bonus Points
                              <span className="block text-xs font-normal text-gray-400 mt-0.5">
                                Flat points on reaching this tier
                              </span>
                            </td>
                            {displayTiers.map((tier) => (
                              <td
                                key={tier.name}
                                className="px-4 py-4 bg-white border-r border-gray-100 text-center"
                              >
                                <input
                                  type="number"
                                  min="0"
                                  className={inputCls}
                                  value={data[tier.name]?.bonus ?? 0}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    if (!val || val.match(/^\d{1,}(\.\d{0,4})?$/)) {
                                      handleOnValueChange(val, tier.name, "bonus");
                                    }
                                  }}
                                />
                              </td>
                            ))}
                          </tr>
                        </>
                      ) : (
                        <>
                          {/* Per Month */}
                          <tr className="border-b border-gray-100">
                            <td className="bg-gray-50 px-5 py-4 font-semibold text-gray-700 border-r border-gray-200">
                              {rangeType}/month
                            </td>
                            {displayTiers.map((tier) => (
                              <td
                                key={tier.name}
                                className="px-4 py-4 bg-white border-r border-gray-100 text-center"
                              >
                                <input
                                  type="number"
                                  className={inputCls}
                                  value={data[tier.name]?.perMonth ?? 0}
                                  onChange={(e) =>
                                    handleOnValueChange(e.target.value, tier.name, "perMonth")
                                  }
                                />
                              </td>
                            ))}
                          </tr>

                          {/* Points Multiplier */}
                          <tr className="border-b border-gray-100">
                            <td className="bg-gray-50 px-5 py-4 font-semibold text-gray-700 border-r border-gray-200">
                              Points Multiplier %
                            </td>
                            {displayTiers.map((tier) => (
                              <td
                                key={tier.name}
                                className="px-4 py-4 bg-white border-r border-gray-100 text-center"
                              >
                                <input
                                  type="text"
                                  className={inputCls}
                                  value={data[tier.name]?.pointsMultiplier ?? 0}
                                  onChange={(e) => {
                                    const amount = e.target.value;
                                    if (!amount || amount.match(/^\d{1,}(\.\d{0,4})?$/)) {
                                      handleOnValueChange(
                                        amount,
                                        tier.name,
                                        "pointsMultiplier",
                                      );
                                    }
                                  }}
                                />
                              </td>
                            ))}
                          </tr>

                          {/* Gift Discount */}
                          <tr className="border-b border-gray-100">
                            <td className="bg-gray-50 px-5 py-4 font-semibold text-gray-700 border-r border-gray-200">
                              Gift Discount %
                            </td>
                            {displayTiers.map((tier) => (
                              <td
                                key={tier.name}
                                className="px-4 py-4 bg-white border-r border-gray-100 text-center"
                              >
                                <input
                                  type="text"
                                  className={inputCls}
                                  value={data[tier.name]?.giftDiscount ?? 0}
                                  onChange={(e) => {
                                    const amount = e.target.value;
                                    if (!amount || amount.match(/^\d{1,}(\.\d{0,4})?$/)) {
                                      handleOnValueChange(
                                        amount,
                                        tier.name,
                                        "giftDiscount",
                                      );
                                    }
                                  }}
                                />
                              </td>
                            ))}
                          </tr>

                          {/* Cashback */}
                          <tr className="border-b border-gray-100">
                            <td className="bg-gray-50 px-5 py-4 font-semibold text-gray-700 border-r border-gray-200">
                              Cashback Option
                            </td>
                            {displayTiers.map((tier) => (
                              <td
                                key={tier.name}
                                className="px-4 py-4 bg-white border-r border-gray-100 text-center"
                              >
                                <button
                                  type="button"
                                  className="flex justify-center items-center gap-1.5 mx-auto cursor-pointer"
                                  onClick={() =>
                                    handleOnValueChange(
                                      !data[tier.name]?.cashback,
                                      tier.name,
                                      "cashback",
                                    )
                                  }
                                >
                                  {data[tier.name]?.cashback ? (
                                    <span className="flex items-center gap-1.5 text-green-600 font-medium">
                                      <BsCheckCircleFill /> Yes
                                    </span>
                                  ) : (
                                    <span className="flex items-center gap-1.5 text-gray-400">
                                      <BsCircle /> No
                                    </span>
                                  )}
                                </button>
                              </td>
                            ))}
                          </tr>

                          {/* Cash Benefit */}
                          <tr className="border-b border-gray-100">
                            <td className="bg-gray-50 px-5 py-4 font-semibold text-gray-700 border-r border-gray-200">
                              Cash Benefit
                            </td>
                            {displayTiers.map((tier) => (
                              <td
                                key={tier.name}
                                className="px-4 py-4 bg-white border-r border-gray-100 text-center"
                              >
                                <input
                                  type="text"
                                  className={inputCls}
                                  value={data[tier.name]?.cash_benefit ?? 0}
                                  onChange={(e) => {
                                    const amount = e.target.value;
                                    if (!amount || amount.match(/^\d{1,}(\.\d{0,4})?$/)) {
                                      handleOnValueChange(
                                        amount,
                                        tier.name,
                                        "cash_benefit",
                                      );
                                    }
                                  }}
                                />
                              </td>
                            ))}
                          </tr>
                        </>
                      )}
                    </tbody>
                  </table>

                  {/* Tier direction legend */}
                  <div className="flex items-center justify-end gap-2 px-4 py-2 bg-gray-50 border-t border-gray-200 text-xs text-gray-400">
                    <span>Entry Tier (Rank {totalTiers})</span>
                    <span>→</span>
                    <span>Top Tier (Rank 1)</span>
                    <span className="ml-2 text-gray-300">|</span>
                    <span>Fill ranges left-to-right with increasing values</span>
                  </div>
                </div>
              )}

              <div className="flex justify-center py-4">
                {isUpdate ? (
                  <button
                    type="submit"
                    onClick={handleUpdate}
                    className="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-8 py-2.5"
                  >
                    Update
                  </button>
                ) : (
                  <button
                    type="submit"
                    onClick={handleSubmit}
                    className="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-8 py-2.5"
                  >
                    Save
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default ManageMembership;
